/**
 * 埋点上报 API
 */

import request from '@/utils/request'
import { getApiBaseUrl } from '@/utils/env'
import { tcGetAppInfo, tcEventTracker, isApp, type AppEventItem } from '@/utils/bridge'
import type { BatchReportData } from '@/lib/event-tracker/@types'
import dayjs from 'dayjs'

/**
 * 后端期望的事件数据格式（H5）
 */
interface BackendEventData {
  event: string
  event_time: string
  platform: string
  os: string
  browser: string
  page_url: string
  page_title: string
  extra: Record<string, unknown>
}

/**
 * App 埋点上报数据格式
 */
interface AppEventData {
  header: Record<string, unknown>
  events: Array<{
    event: string
    event_time: string
    params: Record<string, unknown>
    page_name: string
    event_name: string
    local_time_ms: number
  }>
  user: Record<string, unknown>
  launch: Array<Record<string, unknown>>
  local_time: number
}

/**
 * 通用方法：将 BatchReportData 转换为后端格式
 */
const transformToBackendData = (data: BatchReportData): BackendEventData[] => {
  return data.events.map((event) => {
    return {
      event: event.event,
      event_time: dayjs(event.local_time_ms).toISOString(),
      platform: data.header.platform,
      os: data.header.os_name,
      browser: data.header.browser,
      page_url: (data.header.page_url as string) || window.location.href,
      page_title: (data.header.page_title as string) || document.title,
      extra: {
        header: data.header,
        events: [event],
        user: data.user,
        local_time: data.local_time,
      },
    }
  })
}

/**
 * 批量埋点上报（常规上报）
 * 后端接口：POST /commonfacade/eventlog/upload
 * 请求体：BackendEventData[]
 * 响应：{ status: 200, message: 'success' }
 */
export const reportBatchEvents = (data: BatchReportData) => {
  const backendData = transformToBackendData(data)
  return request.post('/commonfacade/eventlog/upload', backendData, {
    silent: true, // 埋点上报使用静默模式
    skipErrorReport: true, // 防止死循环：埋点接口失败不再触发错误埋点
  })
}

/**
 * 将 BatchReportData 转换为 App 后端格式
 */
const transformToAppBackendData = async (data: BatchReportData): Promise<AppEventData> => {
  console.log(data, '========transformToAppBackendData========')
  try {
    // 获取 App 信息
    const appInfo = await tcGetAppInfo()

    if (!appInfo) {
      throw new Error('获取 App 信息失败：返回数据为空')
    }

    // 直接从 appInfo 获取基础数据（新结构无 extra）
    const header = appInfo.header || {}
    const user = appInfo.user || {}
    const launch = appInfo.launch || []

    // 使用 H5 判断的 platform 覆盖 header 中的 platform
    const finalHeader = {
      ...header,
      platform: data.header.platform, // 使用 H5 环境判断的 platform
    }

    return {
      header: finalHeader,
      events: data.events.map((event) => ({
        event: event.event,
        event_time: dayjs(event.local_time_ms).toISOString(),
        params: event.params || {},
        page_name: (event.params?.page_title as string) || document.title || '',
        event_name: (event.params?.event_name as string) || (event.params?.eventName as string) || '',
        local_time_ms: event.local_time_ms,
      })),
      user: user,
      launch: launch,
      local_time: data.local_time,
    }
  } catch (error) {
    console.error('[Event Tracker] transformToAppBackendData 失败:', error)
    throw error
  }
}

/**
 * App 环境批量埋点上报
 * 后端接口：POST /commonfacade/api/v1/eventlog/uploadBatch
 * 请求体：AppEventData
 * 响应：{ status: 200, message: 'success' }
 */
export const reportAppBatchEvents = async (data: BatchReportData) => {
  const appBackendData = await transformToAppBackendData(data)
  return request.post('/commonfacade/api/v1/eventlog/uploadBatch', appBackendData, {
    silent: true, // 埋点上报使用静默模式
    skipErrorReport: true, // 防止死循环：埋点接口失败不再触发错误埋点
  })
}

/**
 * 使用 sendBeacon 批量上报（用于页面卸载等关键时刻）
 * 适用场景：
 * - 页面卸载（beforeunload/unload/pagehide）
 * - 页面可见性变化（visibilitychange 到 hidden）
 *
 * 优势：
 * - 不阻塞页面卸载
 * - 浏览器保证数据发送
 * - 异步后台发送
 */
export const reportBatchEventsBeacon = (data: BatchReportData): boolean => {
  // 使用通用方法转换数据
  const backendData = transformToBackendData(data)
  const url = getApiBaseUrl() + '/commonfacade/eventlog/uploadNoCry'
  const payload = JSON.stringify(backendData)

  // 检查 sendBeacon 是否可用
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const blob = new Blob([payload], { type: 'application/json' })
    const success = navigator.sendBeacon(url, blob)

    if (success) {
      console.log('[Event Tracker] sendBeacon 发送成功', data.events.length, '条事件')
    } else {
      console.warn('[Event Tracker] sendBeacon 发送失败（可能超过配额）')
    }

    return success
  } else {
    console.warn('[Event Tracker] sendBeacon 不可用')
    return false
  }
}

/**
 * 统一的埋点上报方法（封装 H5/App 两种上报方式）
 *
 * @description
 * 自动根据环境和配置选择合适的上报方式：
 * - App 环境 + useNativeTracker=true：调用客户端代理上报
 * - App 环境 + useNativeTracker=false：使用 HTTP 上报
 * - H5 环境：总是使用 HTTP 上报
 *
 * @param data 埋点数据
 * @param options 配置项
 * @param options.useNativeTracker 是否使用客户端代理上报（默认 false）
 * @returns Promise<void>
 *
 * @example
 * ```ts
 * // 使用 HTTP 上报（默认）
 * await reportEvents(data)
 *
 * // 使用客户端代理上报（App 环境下）
 * await reportEvents(data, { useNativeTracker: true })
 * ```
 */
export const reportEvents = async (
  data: BatchReportData,
  options: { useNativeTracker?: boolean } = {},
): Promise<void> => {
  const { useNativeTracker = false } = options

  try {
    // 判断是否使用客户端代理上报
    if (isApp() && useNativeTracker) {
      // 通常队列中只有一个事件（trackWithNative 立即 flush）
      // 异步上报，不阻塞业务流程（客户端会保证上报完成）
      data.events.forEach((event) => {
        const item: AppEventItem = {
          event: event.event, // 必传
        }

        // 可选字段：有值才添加
        const eventName = (event.params?.event_name as string) || (event.params?.eventName as string)
        if (eventName) item.eventName = eventName

        const pageName = (event.params?.page_title as string) || document.title
        if (pageName) item.pageName = pageName

        if (event.params && Object.keys(event.params).length > 0) {
          item.params = event.params
        }

        // 调用客户端埋点
        tcEventTracker(item)
      })
    } else {
      // H5 环境或 App 不使用客户端代理

      if (isApp()) {
        // App 环境使用 App 接口
        await reportAppBatchEvents(data)
      } else {
        // H5 环境使用 H5 接口
        await reportBatchEvents(data)
      }
    }
  } catch (error) {
    console.error('[Event Tracker] 上报失败:', error)
    // 不要抛出错误，避免影响业务逻辑
  }
}
